import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { useAchievements } from '../contexts/AchievementContext';
import { useNotifications } from '../contexts/NotificationContext';
import apiService from '../services/api';
import HelpDisclosure from '../components/HelpDisclosure';
import ModalPortal from '../components/ModalPortal';
import { requireAuth } from '../utils/authPrompt';
import '../styles/Feed.css';
import '../styles/Notifications.css';

const createPostHelp = [
  {
    question: "What should I post?",
    answer: "Share a dish you cooked, a recipe idea, a result from PrepRight, or a cooking tip for friends."
  },
  {
    question: "Is a photo required?",
    answer: "Yes. You can upload an image file or switch to Image Link and paste a direct image URL."
  },
  {
    question: "Who can see it?",
    answer: "Posts appear in the cooking feed for other users, and friends can interact with likes and comments."
  }
];

const Feed = ({ sidebarOpen }) => {
  const { user } = useUser();
  const { handleApiResponse, isShowingAchievement } = useAchievements();
  const { notifications, loadNotifications, markAllAsRead, clearAllNotifications, removeNotification } = useNotifications();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreatePost, setShowCreatePost] = useState(false);
  const [showMyPosts, setShowMyPosts] = useState(false);
  const [showFollowing, setShowFollowing] = useState(false);
  const [newPost, setNewPost] = useState({
    title: '',
    description: '',
    image: null,
    imagePreview: null,
    imageLink: '',
    useImageLink: false
  });
  const [newComment, setNewComment] = useState({});
  const [expandedComments, setExpandedComments] = useState({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeletePostConfirm, setShowDeletePostConfirm] = useState(false);
  const [showDeleteCommentConfirm, setShowDeleteCommentConfirm] = useState(false);
  const [postToDelete, setPostToDelete] = useState(null);
  const [commentToDelete, setCommentToDelete] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [editPostData, setEditPostData] = useState({
    title: '',
    description: ''
  });
  const [showFriends, setShowFriends] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [myFriends, setMyFriends] = useState([]);
  const [pendingFriendIds, setPendingFriendIds] = useState([]);
  const [pendingFriendActionId, setPendingFriendActionId] = useState(null);

  // Load actual users for friends suggestions
  const loadUsers = useCallback(async () => {
    try {
      const response = await apiService.getAllUsers();
      if (response && response.users) {
        // Get friend IDs to filter them out
        const friendIds = myFriends.map(friend => Number(friend.id));
        const pendingIds = pendingFriendIds.map(Number);
        
        // Filter out current user and existing friends
        const otherUsers = response.users
          .filter(u => Number(u.id) !== Number(user?.id) && !friendIds.includes(Number(u.id)) && !pendingIds.includes(Number(u.id)))
          .map(u => ({
            id: u.id,
            name: u.name || u.email,
            email: u.email,
            discriminator: u.discriminator || '0000',
            profilePicture: u.profilePicture,
            level: u.level || 1,
            points: u.points || 0,
            bio: u.bio || 'Food enthusiast'
          }));
        setSuggestedUsers(otherUsers);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      // Fallback to empty array if API fails
      setSuggestedUsers([]);
    }
  }, [user?.id, myFriends, pendingFriendIds]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Load friend requests
  const loadFriendRequests = useCallback(async () => {
    try {
      const response = await apiService.getFriendRequests();
      if (response && response.success) {
        const outgoingIds = (response.sentRequests || [])
          .map(request => request.receiverId || request.receiver?.id)
          .filter(Boolean)
          .map(Number);
        setPendingFriendIds(outgoingIds);
        return response.requests || [];
      }
    } catch (error) {
      console.error('Failed to load friend requests:', error);
    }
  }, []);

  const loadMyFriends = useCallback(async () => {
    try {
      const response = await apiService.getFriends();
      if (response && response.success) {
        setMyFriends(response.friends || []);
      }
    } catch (error) {
      console.error('Failed to load friends:', error);
    }
  }, []);

  useEffect(() => {
    if (user?.id) {
      loadFriendRequests();
      loadMyFriends();
    }
  }, [user?.id, loadFriendRequests, loadMyFriends]);

  useEffect(() => {
    const handleFriendsRefresh = () => {
      if (user?.id) {
        loadFriendRequests();
        loadMyFriends();
      }
      loadUsers();
    };

    window.addEventListener("friendsRefresh", handleFriendsRefresh);
    return () => window.removeEventListener("friendsRefresh", handleFriendsRefresh);
  }, [user?.id, loadFriendRequests, loadMyFriends, loadUsers]);

  // Search functionality
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      // Search through actual users with more flexible matching
      const query = searchQuery.toLowerCase().trim();
      const results = suggestedUsers.filter(user => {
        const name = (user.name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const discriminator = user.discriminator || '0000';
        const fullUsername = `${name}#${discriminator}`.toLowerCase();
        
        // Search by name, email, or full username#discriminator
        return name.includes(query) || 
               email.includes(query) || 
               fullUsername.includes(query) ||
               query.includes(name);
      });
      
      
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
      showSuccess('Search failed');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAddFriend = async (userId) => {
    if (!requireAuth(user, "Register or log in to send friend requests.")) return;

    try {
      // Send friend request to backend
      const response = await apiService.sendFriendRequest(userId);
      if (response.success) {
        showSuccess('Friend request sent successfully!');
        // Remove user from suggestions to avoid duplicate requests
        setPendingFriendIds(prev => Array.from(new Set([...prev, Number(userId)])));
        setSuggestedUsers(prev => prev.filter(u => u.id !== userId));
        setSearchResults(prev => prev.filter(u => u.id !== userId));
      } else {
        showSuccess(response.message || 'Failed to send friend request');
      }
    } catch (error) {
      console.error('Add friend error:', error);
      showSuccess('Failed to send friend request');
    }
  };

  const handleRemoveFriend = async (friendId) => {
    if (!requireAuth(user, "Register or log in to manage friends.")) return;
    if (!friendId || pendingFriendActionId === `remove-${friendId}`) return;

    try {
      setPendingFriendActionId(`remove-${friendId}`);
      const response = await apiService.removeFriend(friendId);
      if (response.success) {
        showSuccess('Friend removed');
        setMyFriends(prev => prev.filter(friend => Number(friend.id) !== Number(friendId)));
        await loadUsers();
        window.dispatchEvent(new CustomEvent("friendsRefresh"));
      } else {
        showSuccess(response.message || 'Failed to remove friend');
      }
    } catch (error) {
      console.error('Remove friend error:', error);
      showSuccess('Failed to remove friend');
    } finally {
      setPendingFriendActionId(null);
    }
  };

  const handleAcceptFriendRequest = async (notification) => {
    if (!requireAuth(user, "Register or log in to accept friend requests.")) return;
    const requestId = notification?.data?.requestId;
    const actionId = requestId || notification?.id;
    if (!actionId || pendingFriendActionId === actionId) return;

    try {
      setPendingFriendActionId(actionId);
      const response = await apiService.acceptFriendRequest(requestId, notification?.id || null);
      if (response.success) {
        showSuccess('Friend request accepted!');
        if (notification?.id) removeNotification(notification.id);
        await loadNotifications();
        await loadFriendRequests();
        await loadMyFriends();
        window.dispatchEvent(new CustomEvent("friendsRefresh"));
      } else {
        showSuccess(response.message || 'Failed to accept friend request');
      }
    } catch (error) {
      console.error('Accept friend request error:', error);
      showSuccess('Failed to accept friend request');
    } finally {
      setPendingFriendActionId(null);
    }
  };

  const handleDeclineFriendRequest = async (notification) => {
    if (!requireAuth(user, "Register or log in to manage friend requests.")) return;
    const requestId = notification?.data?.requestId;
    const actionId = requestId || notification?.id;
    if (!actionId || pendingFriendActionId === actionId) return;

    try {
      setPendingFriendActionId(actionId);
      const response = await apiService.declineFriendRequest(requestId, notification?.id || null);
      if (response.success) {
        showSuccess('Friend request declined');
        if (notification?.id) removeNotification(notification.id);
        await loadNotifications();
        await loadFriendRequests();
        await loadMyFriends();
        window.dispatchEvent(new CustomEvent("friendsRefresh"));
      } else {
        showSuccess(response.message || 'Failed to decline friend request');
      }
    } catch (error) {
      console.error('Decline friend request error:', error);
      showSuccess('Failed to decline friend request');
    } finally {
      setPendingFriendActionId(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      showSuccess('All notifications marked as read');
    } catch (error) {
      console.error('Mark all read error:', error);
      showSuccess('Failed to mark all notifications as read');
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      await clearAllNotifications();
      showSuccess('All notifications cleared');
    } catch (error) {
      console.error('Clear all notifications error:', error);
      showSuccess('Failed to clear all notifications');
    }
  };

  const formatUsername = (user) => {
    return `${user.name || user.email || 'User'}#${user.discriminator || '0000'}`;
  };

  // Helper function to show success message
  const showSuccess = (message) => {
    // If an achievement is being shown, delay the success message
    if (isShowingAchievement) {
      setTimeout(() => {
        showSuccess(message);
      }, 6000); // Wait 6 seconds (5 for achievement + 1 buffer)
      return;
    }
    
    setSuccessMessage(message);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 5000);
  };

  const loadFeed = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.getFeed();
      if (response && response.posts) {
        setPosts(response.posts);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Failed to load feed:', error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadMyPosts = useCallback(async () => {
    if (!requireAuth(user, "Register or log in to view and manage your posts.")) {
      setShowMyPosts(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await apiService.getMyPosts();
      if (response && response.posts) {
        setPosts(response.posts);
      } else {
        setPosts([]);
      }
    } catch (error) {
      console.error('Failed to load my posts:', error);
      setPosts([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (showMyPosts) {
      loadMyPosts();
    } else {
      loadFeed();
    }
  }, [showMyPosts, loadFeed, loadMyPosts]);

  const handleImageChange = (e) => {
    if (!requireAuth(user, "Register or log in to create cooking feed posts.")) return;

    const file = e.target.files[0];
    if (file) {
      setNewPost({
        ...newPost,
        image: file,
        imagePreview: URL.createObjectURL(file)
      });
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!requireAuth(user, "Register or log in to create cooking feed posts.")) return;

    // Validation
    if (!newPost.title.trim()) {
      setErrorMessage('Please enter a recipe title');
      setShowErrorMessage(true);
      return;
    }

    let imageUrl = '';
    
    setIsLoading(true);
    try {
      if (newPost.useImageLink) {
        // Using image link
        if (!newPost.imageLink.trim()) {
          setErrorMessage('Please enter an image URL');
          setShowErrorMessage(true);
          setIsLoading(false);
          return;
        }
        imageUrl = newPost.imageLink.trim();
      } else {
        // Using file upload
        if (!newPost.image) {
          setErrorMessage('Please select an image file');
          setShowErrorMessage(true);
          setIsLoading(false);
          return;
        }
        
        // Upload the image first
        const formData = new FormData();
        formData.append('image', newPost.image);
        
        const uploadResponse = await apiService.uploadFeedImage(formData);
        imageUrl = uploadResponse.imageUrl;
      }

      // Create the post with the image URL
      const postData = {
        title: newPost.title.trim(),
        description: newPost.description.trim(),
        imageUrl: imageUrl
      };
      
      const response = await apiService.createPostSimple(postData);

      // Handle achievement notifications
      handleApiResponse(response);
      
      // Show success message for post creation
      if (response.pointsAwarded === false) {
        showSuccess(`Post created! (You've already earned points for posting today)`);
      } else if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Post created successfully! +${response.pointsEarned} points earned!`);
      } else {
        showSuccess('Post created successfully!');
      }

      // Reset form
      setNewPost({ 
        title: '', 
        description: '', 
        image: null, 
        imagePreview: null, 
        imageLink: '', 
        useImageLink: false 
      });
      setShowCreatePost(false);

      // Reload feed
      await loadFeed();
      
    } catch (error) {
      console.error('Failed to create post:', error);
      setErrorMessage('Failed to create post. Please try again.');
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeletePost = async (postId) => {
    setPostToDelete(postId);
    setShowDeletePostConfirm(true);
  };

  const confirmDeletePost = async () => {
    if (!postToDelete) return;

    try {
      await apiService.deletePost(postToDelete);
      setPosts(posts.filter(post => post.id !== postToDelete));
      setShowDeletePostConfirm(false);
      setPostToDelete(null);
    } catch (error) {
      console.error('Failed to delete post:', error);
      setErrorMessage('Failed to delete post. Please try again.');
      setShowErrorMessage(true);
      setShowDeletePostConfirm(false);
    }
  };

  const handleLikePost = async (postId) => {
    if (!requireAuth(user, "Register or log in to like posts.")) return;

    try {
      const response = await apiService.likePost(postId);
      
      // Handle achievement notifications
      handleApiResponse(response);
      
      // Show success message for likes
      if (response.pointsAwarded === false) {
        showSuccess(`Post liked! (You've already earned points for liking today)`);
      } else if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Post liked! +${response.pointsEarned} points earned!`);
      } else {
        showSuccess(`Post liked!`);
      }
      
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, isLikedByUser: response.isLiked, likesCount: response.likesCount }
          : post
      ));
    } catch (error) {
      console.error('Failed to like post:', error);
    }
  };

  const handleAddComment = async (postId) => {
    if (!requireAuth(user, "Register or log in to comment on posts.")) return;

    const content = newComment[postId];
    if (!content.trim()) return;

    try {
      const response = await apiService.addComment(postId, content);
      
      // Handle achievement notifications
      handleApiResponse(response);
      
      // Show success message for comments
      if (response.pointsAwarded === false) {
        showSuccess(`Comment added! (You've already earned points for commenting today)`);
      } else if (response.pointsEarned && response.pointsEarned > 0) {
        showSuccess(`Comment added! +${response.pointsEarned} points earned!`);
      } else {
        showSuccess(`Comment added!`);
      }
      
      setPosts(posts.map(post => 
        post.id === postId 
          ? { ...post, comments: [...post.comments, response.comment] }
          : post
      ));
      setNewComment({ ...newComment, [postId]: '' });
    } catch (error) {
      console.error('Failed to add comment:', error);
    }
  };

  const handleDeleteComment = async (commentId, postId) => {
    setCommentToDelete({ commentId, postId });
    setShowDeleteCommentConfirm(true);
  };

  const confirmDeleteComment = async () => {
    if (!commentToDelete) return;

    try {
      await apiService.deleteComment(commentToDelete.commentId);
      setPosts(posts.map(post =>
        post.id === commentToDelete.postId
          ? { ...post, comments: post.comments.filter(comment => comment.id !== commentToDelete.commentId) }
          : post
      ));
      setShowDeleteCommentConfirm(false);
      setCommentToDelete(null);
    } catch (error) {
      console.error('Failed to delete comment:', error);
      setErrorMessage('Failed to delete comment. Please try again.');
      setShowErrorMessage(true);
      setShowDeleteCommentConfirm(false);
    }
  };

  const handleSaveEdit = async (postId) => {
    if (!editPostData.title.trim()) {
      setErrorMessage('Please enter a post title');
      setShowErrorMessage(true);
      return;
    }

    try {
      await apiService.updatePost(postId, {
        title: editPostData.title.trim(),
        description: editPostData.description.trim()
      });
      
      setPosts(posts.map(post =>
        post.id === postId
          ? { ...post, title: editPostData.title.trim(), description: editPostData.description.trim() }
          : post
      ));
      
      setEditingPost(null);
      setEditPostData({title: '', description: ''});
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Failed to update post:', error);
      setErrorMessage('Failed to update post. Please try again.');
      setShowErrorMessage(true);
    }
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({
      ...prev,
      [postId]: !prev[postId]
    }));
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const postDate = new Date(dateString);
    const diffInSeconds = Math.floor((now - postDate) / 1000);

    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  return (
    <div className={`instagram-feed-container ${sidebarOpen ? 'sidebar-open' : ''}`}>
      {/* Instagram Layout */}
      <div className="instagram-layout">
        {/* Left Sidebar - Instagram Style */}
        <div className="instagram-left-sidebar">
          <nav className="sidebar-nav">
            <button 
              className={`nav-item ${!showMyPosts ? 'active' : ''}`}
              onClick={() => setShowMyPosts(false)}
            >
              <span className="nav-icon">{'\uD83D\uDD0D'}</span>
              <span className="nav-text">Discover</span>
            </button>
            <button 
              className={`nav-item ${showMyPosts ? 'active' : ''}`}
              onClick={() => {
                if (requireAuth(user, "Register or log in to view and manage your posts.")) {
                  setShowMyPosts(true);
                }
              }}
            >
              <span className="nav-icon">{'\uD83D\uDDBC\uFE0F'}</span>
              <span className="nav-text">My Posts</span>
            </button>
            <button 
              className="nav-item"
              onClick={() => {
                if (requireAuth(user, "Register or log in to share your cooking.")) {
                  setShowCreatePost(true);
                }
              }}
            >
              <span className="nav-icon">+</span>
              <span className="nav-text">Create Post</span>
            </button>
            <button 
              className={`nav-item ${showFriends ? 'active' : ''}`}
              onClick={() => {
                if (requireAuth(user, "Register or log in to view and manage friends.")) {
                  setShowFriends(!showFriends);
                }
              }}
            >
              <span className="nav-icon">{'\uD83D\uDC65'}</span>
              <span className="nav-text">Friends</span>
            </button>
            <button 
              className={`nav-item ${showNotifications ? 'active' : ''}`}
              onClick={() => setShowNotifications(!showNotifications)}
            >
              <span className="nav-icon">{'\uD83D\uDD14'}</span>
              <span className="nav-text">Notifications</span>
            </button>
          </nav>
        </div>

        {/* Center Feed */}
        <div className="instagram-center-feed">
          <div className="feed-tabs">
            <button 
              className={`feed-tab ${!showMyPosts && !showFollowing ? 'active' : ''}`}
              onClick={() => {
                setShowMyPosts(false);
                setShowFollowing(false);
              }}
            >
              For you
            </button>
            <button 
              className={`feed-tab ${showFollowing ? 'active' : ''}`}
              onClick={() => {
                setShowMyPosts(false);
                setShowFollowing(true);
              }}
            >
              Following
            </button>
            <button 
              className={`feed-tab ${showMyPosts ? 'active' : ''}`}
              onClick={() => {
                if (requireAuth(user, "Register or log in to view and manage your posts.")) {
                  setShowMyPosts(true);
                  setShowFollowing(false);
                }
              }}
            >
              My Posts
            </button>
          </div>

          <div className="mobile-feed-actions" aria-label="Feed mobile actions">
            <button
              type="button"
              className="mobile-feed-action mobile-feed-action--primary"
              onClick={() => {
                if (requireAuth(user, "Register or log in to share your cooking.")) {
                  setShowCreatePost(true);
                }
              }}
            >
              <span className="mobile-feed-action__icon">{'\uD83D\uDDBC\uFE0F'}</span>
              <span>Create Post</span>
            </button>
            <button
              type="button"
              className="mobile-feed-action"
              onClick={() => {
                if (requireAuth(user, "Register or log in to view and manage your posts.")) {
                  setShowMyPosts(true);
                  setShowFollowing(false);
                }
              }}
            >
              <span className="mobile-feed-action__icon">{'\uD83D\uDDBC\uFE0F'}</span>
              <span>My Posts</span>
            </button>
            <button
              type="button"
              className="mobile-feed-action"
              onClick={() => setShowFriends(true)}
            >
              <span className="mobile-feed-action__icon">{'\uD83D\uDC65'}</span>
              <span>Friends</span>
            </button>
            <button
              type="button"
              className="mobile-feed-action"
              onClick={() => setShowNotifications(true)}
            >
              <span className="mobile-feed-action__icon">{'\uD83D\uDD14'}</span>
              <span>Alerts</span>
              {notifications.filter((notification) => !notification.isRead).length > 0 && (
                <span className="mobile-feed-action__badge">
                  {notifications.filter((notification) => !notification.isRead).length}
                </span>
              )}
            </button>
          </div>

          {/* Feed Content */}
          <div className="feed-content">
        {isLoading ? (
          <div className="loading">Loading posts...</div>
        ) : posts.length === 0 ? (
          <div className="empty-feed">
            <h3>{showMyPosts ? 'No posts in My Posts yet' : 'No posts yet'}</h3>
            <p>{showMyPosts ? 'Create your first post and it will appear here with edit/delete options.' : 'Be the first to share your cooking!'}</p>
          </div>
        ) : (
          <div className="posts-container">
            {(() => {
              const filteredPosts = posts.filter(post => {
                const userIdMatch = user ? Number(post.userId) === Number(user.id) : false;
                
                if (showMyPosts) {
                  // Show only current user's posts
                  return userIdMatch;
                } else if (showFollowing) {
                  // Show only friends' posts
                  const friendIds = myFriends.map(friend => friend.id);
                  return friendIds.includes(Number(post.userId));
                } else {
                  // Show all posts (For you tab)
                  return true;
                }
              });

              if (showMyPosts && filteredPosts.length === 0) {
                return (
                  <div className="no-posts-message">
                    <h3>Your My Posts section is empty</h3>
                    <p>Create a post to manage it here. You can edit or delete your posts anytime.</p>
                  </div>
                );
              }

              // Show message if Following tab has no posts from friends
              if (showFollowing && filteredPosts.length === 0) {
                return (
                  <div className="no-posts-message">
                    <h3>No posts from friends yet</h3>
                    <p>Your friends haven't posted anything recently. Check out the "For you" tab to discover new content!</p>
                  </div>
                );
              }

              return filteredPosts.map(post => {
              const userIdMatch = user ? Number(post.userId) === Number(user.id) : false;
              
              return (
              <div key={post.id} className="post-card">
                {/* Post Header */}
                <div className="post-header">
                  <div className="user-info">
                    <div className="user-avatar">
                      {post.User?.profilePicture ? (
                        <img 
                          src={apiService.getImageUrl(post.User.profilePicture)} 
                          alt={post.User?.name || 'User'} 
                          className="profile-avatar-image"
                        />
                      ) : (
                        <span>{post.User?.name ? post.User.name[0].toUpperCase() : (post.User?.email ? post.User.email[0].toUpperCase() : 'U')}</span>
                      )}
                    </div>
                    <div className="user-details">
                      <span className="username">
                        {post.User?.name || post.User?.email || 'User'}
                        {post.User?.username && post.User?.discriminator && (
                          <span className="user-code">#{post.User.discriminator}</span>
                        )}
                      </span>
                      <span className="post-time">{formatTimeAgo(post.createdAt)}</span>
                    </div>
                  </div>
                  {showMyPosts && userIdMatch && (
                    <div className="post-actions-inline">
                      <button 
                        className="edit-post-btn"
                        onClick={() => {
                          setEditingPost(post.id);
                          setEditPostData({
                            title: post.title,
                            description: post.description || ''
                          });
                        }}
                        title="Edit post"
                      >Edit</button>
                      <button 
                        className="delete-post-btn-inline"
                        onClick={() => handleDeletePost(post.id)}
                        title="Delete post"
                      >Delete</button>
                    </div>
                  )}
                </div>

                {/* Post Image */}
                <div className="post-image">
                  <img 
                    src={apiService.getImageUrl(post.imageUrl)} 
                    alt={post.title}
                    onError={(e) => {
                      e.target.src = 'https://via.placeholder.com/400x300/2e7d32/ffffff?text=Image+Not+Found';
                    }}
                  />
                </div>

                {/* Post Actions */}
                <div className="post-actions">
                  <button 
                    className={`like-btn ${post.isLikedByUser ? 'liked' : ''}`}
                    onClick={() => handleLikePost(post.id)}
                  >
                    {post.isLikedByUser ? '\u2764\uFE0F' : '\uD83E\uDD0D'} {post.likesCount}
                  </button>
                  <button 
                    className="comment-btn"
                    onClick={() => toggleComments(post.id)}
                  >
{'\uD83D\uDCAC'} {post.comments.length}
                  </button>
                </div>

                {/* Post Content */}
                <div className="post-content">
                  {editingPost === post.id ? (
                    <div className="edit-post-form">
                      <input
                        type="text"
                        value={editPostData.title}
                        onChange={(e) => setEditPostData({...editPostData, title: e.target.value})}
                        className="edit-title-input"
                        placeholder="Post title"
                      />
                      <textarea
                        value={editPostData.description}
                        onChange={(e) => setEditPostData({...editPostData, description: e.target.value})}
                        className="edit-description-input"
                        placeholder="Post description"
                        rows="3"
                      />
                      <div className="edit-actions">
                        <button 
                          className="save-edit-btn"
                          onClick={() => handleSaveEdit(post.id)}
                        >
                          Save
                        </button>
                        <button 
                          className="cancel-edit-btn"
                          onClick={() => {
                            setEditingPost(null);
                            setEditPostData({title: '', description: ''});
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <h3 className="post-title">{post.title}</h3>
                      {post.description && (
                        <p className="post-description">{post.description}</p>
                      )}
                    </>
                  )}
                </div>

                {/* Comments Section */}
                {expandedComments[post.id] && (
                  <div className="comments-section">
                    {/* Add Comment */}
                    <div className="add-comment">
                      <input
                        type="text"
                        placeholder="Add a comment..."
                        value={newComment[post.id] || ''}
                        onChange={(e) => setNewComment({...newComment, [post.id]: e.target.value})}
                        onKeyPress={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                      />
                      <button onClick={() => handleAddComment(post.id)}>
                        Post
                      </button>
                    </div>

                    {/* Comments List */}
                    <div className="comments-list">
                      {post.comments.map(comment => (
                        <div key={comment.id} className="comment">
                          <div className="comment-user">
                            <span className="comment-username">
                              {comment.User?.name || comment.User?.email || 'User'}
                            </span>
                            <span className="comment-time">
                              {formatTimeAgo(comment.createdAt)}
                            </span>
                          </div>
                          <div className="comment-content">
                            <p>{comment.content}</p>
                            {user && comment.userId === user.id && (
                              <button 
                                className="delete-comment-btn"
                                onClick={() => handleDeleteComment(comment.id, post.id)}
                                title="Delete comment"
                              >
                                &times;
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
            });
            })()}
          </div>
        )}

        {/* Friends Modal */}
        {showFriends && (
          <div className="friends-modal-overlay">
            <div className="friends-modal">
              <div className="friends-header">
                <div>
                  <h2>Cooking Friends</h2>
                  <p>Manage your circle and find people to cook with.</p>
                </div>
                <button 
                  className="close-friends-btn"
                  onClick={() => setShowFriends(false)}
                >
                  &times;
                </button>
              </div>

            <div className="friends-modal-body">
            {/* My Friends Section */}
            <div className="my-friends-section friends-panel friends-panel--primary">
              <div className="friends-section-heading">
                <h3>Your Friends ({myFriends.length})</h3>
                <p>People already connected to your account.</p>
              </div>
              {myFriends && myFriends.length > 0 ? (
                <div className="friends-grid friends-grid--compact">
                  {myFriends.map(friend => (
                    <div key={friend.id} className="friend-card friend-card--managed">
                      <div className="friend-avatar">
                        {friend.profilePicture ? (
                          <img src={apiService.getImageUrl(friend.profilePicture)} alt={friend.name} />
                        ) : (
                          <div className="avatar-initial">{friend.name?.charAt(0) || 'U'}</div>
                        )}
                      </div>
                      <div className="friend-info">
                        <h4 className="friend-name">{formatUsername(friend)}</h4>
                        <p className="friend-bio">{friend.bio || 'Cooking friend'}</p>
                        <div className="friend-stats">
                          <span className="friend-level">Level {friend.level || 1}</span>
                          <span className="friend-points">{friend.points || 0} points</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-remove-friend"
                        disabled={pendingFriendActionId === `remove-${friend.id}`}
                        onClick={() => handleRemoveFriend(friend.id)}
                      >
                        {pendingFriendActionId === `remove-${friend.id}` ? 'Removing...' : 'Unfriend'}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-friends-state">
                  <p>No friends yet. Search below or add someone from suggestions.</p>
                </div>
              )}
            </div>

            {/* Search Section */}
            <div className="search-section friends-panel">
              <div className="friends-section-heading">
                <h3>Add Friends</h3>
                <p>Search by name, email, or username code.</p>
              </div>
              <form onSubmit={handleSearch} className="search-form">
                <div className="search-input-group">
                  <input
                    type="text"
                    placeholder="Search by username or username#discriminator"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                  />
                  <button type="submit" className="btn-search" disabled={searchLoading}>
                    {searchLoading ? 'Searching...' : 'Search'}
                  </button>
                </div>
              </form>

              {searchResults.length > 0 && (
                <div className="search-results">
                  <h4>Search Results</h4>
                  <div className="results-grid">
                    {searchResults.map(user => (
                      <div key={user.id} className="user-card">
                        <div className="user-avatar">
                          {user.profilePicture ? (
                            <img src={apiService.getImageUrl(user.profilePicture)} alt={user.name} />
                          ) : (
                            <div className="avatar-initial">{user.name?.charAt(0) || 'U'}</div>
                          )}
                        </div>
                        <div className="user-info">
                          <h4 className="user-name">{formatUsername(user)}</h4>
                          <p className="user-bio">{user.bio}</p>
                          <div className="user-stats">
                            <span className="user-level">{user.level}</span>
                            <span className="user-points">{user.points} points</span>
                          </div>
                        </div>
                        <div className="user-actions">
                          <button 
                            className="btn-add-friend" 
                            onClick={() => handleAddFriend(user.id)}
                            disabled={searchLoading}
                          >
                            Add Friend
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {searchQuery && searchResults.length === 0 && !searchLoading && (
                <div className="empty-state">
                  <p>No users found matching "{searchQuery}"</p>
                </div>
              )}
            </div>

            {/* Suggested Users */}
            <div className="suggestions-section friends-panel">
              <div className="friends-section-heading">
                <h3>People To Add</h3>
                <p>Users who are not already friends or pending.</p>
              </div>
              {suggestedUsers.length > 0 ? (
              <div className="suggestions-grid">
                {suggestedUsers.slice(0, 12).map(user => (
                  <div key={user.id} className="user-card">
                    <div className="user-avatar">
                      {user.profilePicture ? (
                        <img src={apiService.getImageUrl(user.profilePicture)} alt={user.name} />
                      ) : (
                        <div className="avatar-initial">{user.name?.charAt(0) || 'U'}</div>
                      )}
                    </div>
                    <div className="user-info">
                      <h4 className="user-name">{formatUsername(user)}</h4>
                      <p className="user-bio">{user.bio}</p>
                      <div className="user-stats">
                        <span className="user-level">{user.level}</span>
                        <span className="user-points">{user.points} points</span>
                      </div>
                    </div>
                    <div className="user-actions">
                      <button 
                        className="btn-add-friend" 
                        onClick={() => handleAddFriend(user.id)}
                        disabled={searchLoading}
                      >
                        Add Friend
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              ) : (
                <div className="empty-friends-state">
                  <p>No new suggestions right now.</p>
                </div>
              )}
            </div>
            </div>

            </div>
          </div>
        )}

        {/* Notifications Modal */}
        {showNotifications && (
          <div className="notifications-modal-overlay">
            <div className="notifications-modal">
              <div className="notifications-header">
                <h2>Notifications</h2>
                <div className="notifications-header-actions">
                  <button 
                    className="mark-all-read-btn"
                    onClick={handleMarkAllRead}
                  >
                    Mark all read
                  </button>
                  <button 
                    className="clear-all-notifications-btn"
                    onClick={handleClearAllNotifications}
                  >
                    Clear all
                  </button>
                  <button 
                    className="close-notifications-btn"
                    onClick={() => setShowNotifications(false)}
                  >
                    &times;
                  </button>
                </div>
              </div>
              <div className="notifications-content">
                {/* Friend Requests Section */}
                {notifications && notifications.filter(n => n.type === 'friend_request').length > 0 && (
                  <div className="friend-requests-section">
                    <h3>Friend Requests ({notifications.filter(n => n.type === 'friend_request').length})</h3>
                    {notifications
                      .filter(notification => notification.type === 'friend_request')
                      .map((notification) => {
                        const requestId = notification.data?.requestId;
                        const actionId = requestId || notification.id;
                        const isFriendActionPending = pendingFriendActionId === actionId;

                        return (
                        <div key={notification.id} className="friend-request-item">
                          <div className="friend-request-info">
                            <div className="friend-request-avatar">
                              {notification.data?.sender?.profilePicture ? (
                                <img 
                                  src={apiService.getImageUrl(notification.data.sender.profilePicture)} 
                                  alt={notification.data.sender.name}
                                  className="friend-request-avatar-img"
                                />
                              ) : (
                                <div className="friend-request-avatar-initial">
                                  {notification.data?.sender?.name?.charAt(0).toUpperCase() || 'U'}
                                </div>
                              )}
                            </div>
                            <div className="friend-request-details">
                              <div className="friend-request-name">
                                {notification.data?.sender?.name}#{notification.data?.sender?.discriminator}
                              </div>
                              <div className="friend-request-message">
                                Wants to be your friend
                              </div>
                            </div>
                          </div>
                          <div className="friend-request-actions">
                            <button 
                              className="accept-friend-btn"
                              disabled={isFriendActionPending}
                              onClick={() => handleAcceptFriendRequest(notification)}
                            >
                              Accept
                            </button>
                            <button 
                              className="decline-friend-btn"
                              disabled={isFriendActionPending}
                              onClick={() => handleDeclineFriendRequest(notification)}
                            >
                              Decline
                            </button>
                          </div>
                        </div>
                        );
                      })}
                  </div>
                )}

                {/* Regular Notifications Section */}
                {notifications && notifications.filter(n => n.type !== 'friend_request').length > 0 ? (
                  <div className="regular-notifications-section">
                    <h3>Other Notifications</h3>
                    {notifications
                      .filter(notification => notification.type !== 'friend_request')
                      .map((notification) => (
                      <div key={notification.id} className="notification-item">
                        <div className="notification-icon">
                          {notification.type === 'achievement' && '\uD83C\uDFC6'}
                          {notification.type === 'level_up' && '\u2B50'}
                          {notification.type === 'like' && '\u2764\uFE0F'}
                          {notification.type === 'comment' && '\uD83D\uDCAC'}
                          {notification.type === 'system' && '\uD83D\uDD14'}
                          {notification.type === 'friend_request' && '\uD83D\uDC65'}
                        </div>
                        <div className="notification-content">
                          <div className="notification-title">{notification.title}</div>
                          <div className="notification-message">{notification.message}</div>
                          <div className="notification-time">
                            {new Date(notification.timestamp || notification.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-notifications">
                    <div className="no-notifications-icon">{'\uD83D\uDD14'}</div>
                    <p>No notifications yet</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="instagram-right-sidebar">
          {/* User Profile Section */}
          <div className="user-profile-section">
            <div className="user-avatar-large">
              {user?.profilePicture ? (
                <img 
                  src={apiService.getImageUrl(user.profilePicture)} 
                  alt={user?.name || 'User'} 
                  className="profile-avatar-image"
                />
              ) : (
                <span>{user?.name ? user.name[0].toUpperCase() : (user?.email ? user.email[0].toUpperCase() : 'U')}</span>
              )}
            </div>
            <div className="user-info">
              <div className="username">
                {user?.name || user?.email || 'User'}
                {user?.username && user?.discriminator && (
                  <span className="user-code">#{user.discriminator}</span>
                )}
              </div>
            </div>
          </div>

          {/* Suggestions Section */}
          <div className="suggestions-section">
            <div className="suggestions-header">
              <span className="suggestions-title">Suggestions For You</span>
              <button className="see-all-btn" onClick={() => {
                if (requireAuth(user, "Register or log in to view and add friends.")) {
                  setShowFriends(true);
                }
              }}>See All</button>
            </div>
            <div className="suggestions-list">
              {suggestedUsers.slice(0, 3).map(user => (
                <div key={user.id} className="suggestion-item">
                  <div className="suggestion-avatar">
                    {user.profilePicture ? (
                      <img src={apiService.getImageUrl(user.profilePicture)} alt={user.name} />
                    ) : (
                      <div className="avatar-initial">{user.name?.charAt(0) || 'U'}</div>
                    )}
                  </div>
                  <div className="suggestion-info">
                    <div className="suggestion-username">{formatUsername(user)}</div>
                    <div className="suggestion-followed">{user.level} - {user.points} points</div>
                  </div>
                  <button className="follow-btn" onClick={() => handleAddFriend(user.id)}>Add</button>
                </div>
              ))}
            </div>
          </div>

          {/* Footer Links */}
          <div className="footer-links">
            <div className="footer-links-row">
              <button className="footer-link-btn">About</button>
              <button className="footer-link-btn">Help</button>
              <button className="footer-link-btn">Press</button>
              <button className="footer-link-btn">API</button>
              <button className="footer-link-btn">Jobs</button>
              <button className="footer-link-btn">Privacy</button>
              <button className="footer-link-btn">Terms</button>
              <button className="footer-link-btn">Locations</button>
            </div>
            <div className="footer-links-row">
              <button className="footer-link-btn">Top Accounts</button>
              <button className="footer-link-btn">Hashtags</button>
              <button className="footer-link-btn">Language</button>
            </div>
            <div className="copyright">
              &copy; 2024 PrepRight
            </div>
          </div>
        </div>
      </div>

      {/* Create Post Modal - New Design */}
      {showCreatePost && (
        <div className="feed-modal-overlay">
          <div className="feed-modal-container">
            <div className="feed-modal-header">
              <h2 className="feed-modal-title">SHARE YOUR COOKING</h2>
              <div className="feed-modal-subheader">
                <span className="feed-kitchen">SOCIAL COOKING</span>
                <HelpDisclosure title="Create post help" items={createPostHelp} />
              </div>
              <button 
                className="feed-modal-close"
                onClick={() => setShowCreatePost(false)}
              >
                &times;
              </button>
            </div>
            <div className="feed-modal-content">
              <form onSubmit={handleCreatePost} className="create-post-form">
              <div className="form-group">
                <label>Recipe Title *</label>
                <input
                  type="text"
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  placeholder="What did you cook?"
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newPost.description}
                  onChange={(e) => setNewPost({...newPost, description: e.target.value})}
                  placeholder="Tell us about your dish, ingredients, or cooking process..."
                  rows="4"
                />
              </div>
              <div className="form-group">
                <label>Photo *</label>
                <div className="image-upload-options">
                  <div className="upload-toggle">
                    <button
                      type="button"
                      className={`toggle-btn ${!newPost.useImageLink ? 'active' : ''}`}
                      onClick={() => setNewPost({...newPost, useImageLink: false, imageLink: ''})}
                    >
                      Upload File
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${newPost.useImageLink ? 'active' : ''}`}
                      onClick={() => setNewPost({...newPost, useImageLink: true, image: null, imagePreview: null})}
                    >
                      Image Link
                    </button>
                  </div>
                  
                  {!newPost.useImageLink ? (
                    <div className="image-upload">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        required={!newPost.useImageLink}
                      />
                      {newPost.imagePreview && (
                        <div className="image-preview">
                          <img src={newPost.imagePreview} alt="Preview" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="image-link-input">
                      <input
                        type="url"
                        placeholder="Paste image URL here..."
                        value={newPost.imageLink}
                        onChange={(e) => setNewPost({...newPost, imageLink: e.target.value})}
                        required={newPost.useImageLink}
                      />
                      {newPost.imageLink && (
                        <div className="image-preview">
                          <img src={newPost.imageLink} alt="Preview" onError={(e) => e.target.style.display = 'none'} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className="feed-modal-footer">
                <button type="button" onClick={() => setShowCreatePost(false)} className="feed-cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={isLoading} className="feed-submit-btn">
                  {isLoading ? 'Sharing...' : 'Share Post'}
                </button>
              </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Notification */}
      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">{'\u2705'}</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>Post created successfully!</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>&times;</button>
          </div>
        </div>
      )}

      {/* Error Notification */}
      {showErrorMessage && (
        <div className="error-notification">
          <div className="error-content">
            <span className="error-icon">{'\u26A0\uFE0F'}</span>
            <div className="error-text">
              <strong>Error</strong>
              <span>{errorMessage}</span>
            </div>
            <button className="error-close" onClick={() => setShowErrorMessage(false)}>&times;</button>
          </div>
        </div>
      )}

      {/* Delete Post Confirmation Modal */}
      {showDeletePostConfirm && (
        <ModalPortal>
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header delete-header">
              <h3>Delete Post</h3>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowDeletePostConfirm(false);
                  setPostToDelete(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-content">
                <div className="delete-icon">{'\u26A0\uFE0F'}</div>
                <div className="delete-text">
                  <h4>Are you sure you want to delete this post?</h4>
                  <p className="delete-warning">This action cannot be undone. All comments and likes will be permanently deleted.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowDeletePostConfirm(false);
                  setPostToDelete(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeletePost}
                className="btn-danger"
              >
                Delete Post
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Delete Comment Confirmation Modal */}
      {showDeleteCommentConfirm && (
        <ModalPortal>
        <div className="modal-overlay">
          <div className="modal-content delete-confirm-modal">
            <div className="modal-header delete-header">
              <h3>Delete Comment</h3>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowDeleteCommentConfirm(false);
                  setCommentToDelete(null);
                }}
              >
                &times;
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-content">
                <div className="delete-icon">{'\u26A0\uFE0F'}</div>
                <div className="delete-text">
                  <h4>Are you sure you want to delete this comment?</h4>
                  <p className="delete-warning">This action cannot be undone.</p>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => {
                  setShowDeleteCommentConfirm(false);
                  setCommentToDelete(null);
                }}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDeleteComment}
                className="btn-danger"
              >
                Delete Comment
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}

      {/* Achievement-Style Success Notification */}
      {showSuccessMessage && (
        <div className="success-notification">
          <div className="success-content">
            <span className="success-icon">{'\u2705'}</span>
            <div className="success-text">
              <strong>Success!</strong>
              <span>{successMessage}</span>
            </div>
            <button className="success-close" onClick={() => setShowSuccessMessage(false)}>&times;</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feed;



